import React, { useEffect, useRef, useState } from 'react';

import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ChangeHistoryOutlinedIcon from '@mui/icons-material/ChangeHistoryOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import FavoriteOutlinedIcon from '@mui/icons-material/FavoriteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CardMedia,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  Cell,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Label,
  CartesianGrid,
  Tooltip,
  TooltipProps,
  ResponsiveContainer
} from 'recharts';

import noImage from '../../components/assets/noImage.svg';
import {
  gray10,
  gray100,
  gray70,
  gray80,
  gray90,
  green10,
  green50,
  pink,
  primary10,
  primaryColor,
  white
} from '../../constants/Colors';

import { ItemProps } from './MaterialCard';

const styles = {
  co2ValueIcon: {
    color: primaryColor,
    height: '12px',
    width: '12px',
    verticalAlign: 'middle'
  },
  hover: {
    backgroundColor: white,
    border: `1px solid ${gray80}`,
    boxShadow: '0 2px 4px rgba(0,0,0,.2)',
    padding: '16px',
    pointerEvents: 'auto',
    position: 'relative',
    width: '300px'
  },
  hoverDataLink: {
    '&:hover': {
      opacity: 0.7
    },
    textDecoration: 'none'
  },
  hoverDataImage: {
    height: '80px',
    width: '80px'
  },
  hoverDataText: {
    color: gray100,
    fontSize: '14px'
  },
  hoverDataTitle: {
    color: gray100,
    fontSize: '14px',
    fontWeight: 500
  },
  icon: {
    border: `1px solid ${gray70}`,
    borderRadius: '4px',
    color: gray90,
    height: '18px',
    p: '7px 12px',
    width: '18px'
  },
  table: {
    '& thead th': {
      bgcolor: primary10,
      border: `1px solid ${gray70}`,
      color: gray100,
      fontSize: '14px',
      height: '30px',
      p: '0 16px'
    },
    '& tbody td': {
      border: `1px solid ${gray70}`,
      color: gray100,
      fontSize: '14px',
      height: '15px',
      p: '4px 16px'
    },
    '& tbody tr:hover td': {
      bgcolor: gray10,
      cursor: 'pointer'
    }
  },
  tableCellItemButton: {
    width: '124px'
  },
  tableCellItemCompany: {
    minWidth: '98px'
  },
  tableCellItemEmissionIntensity: {
    minWidth: '88px'
  },
  tableCellItemPrice: {
    minWidth: '47px'
  },
  tableCellItemReduceRate: {
    minWidth: '52px'
  },
  tableCellItemSpecification: {
    minWidth: '120px'
  },
  tableCellText: {
    color: gray100,
    fontSize: '14px'
  },
  tableCellTextBlue: {
    color: primaryColor,
    fontSize: '14px',
    fontWidth: 700
  },
  tableOverflow: {
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  tableSticky: {
    position: 'sticky',
    zIndex: 1,
    left: 0,
    background: white,
    boxShadow: '5px 2px 5px grey',
    borderRight: '2px solid black'
  }
};

interface Props {
  list: ItemProps[];
  addMylistFun?: (productId: number, productName: string) => Promise<void>;
  deleteMylistFun?: (productId: number, productName: string) => Promise<void>;
  addFavoriteFun?: (productId: number, productName: string) => Promise<void>;
  deleteFavoriteFun?: (productId: number, productName: string) => Promise<void>;
  offset: number;
}

const MyListAndFavorite: React.FC<{
  row: ItemProps;
  deleteMylistFun:
    | ((productId: number, productName: string) => Promise<void>)
    | undefined;
  addMylistFun:
    | ((productId: number, productName: string) => Promise<void>)
    | undefined;
  deleteFavoriteFun:
    | ((productId: number, productName: string) => Promise<void>)
    | undefined;
  addFavoriteFun:
    | ((productId: number, productName: string) => Promise<void>)
    | undefined;
}> = ({
  row,
  deleteMylistFun,
  addMylistFun,
  deleteFavoriteFun,
  addFavoriteFun
}) => {
  const [displayMylistFlg, setDisplayMylistFlg] = useState<boolean>(false);
  const [displayFavoriteFlg, setDisplayFavoriteFlg] = useState<boolean>(false);

  useEffect(() => {
    setDisplayMylistFlg(row.myListFlg);
  }, [row.myListFlg]);

  useEffect(() => {
    setDisplayFavoriteFlg(row.favoriteFlg);
  }, [row.favoriteFlg]);

  return (
    <>
      {displayMylistFlg ? (
        <AssignmentIcon
          sx={[styles.icon, { color: primaryColor }]}
          onClick={(e) => {
            e.stopPropagation();
            deleteMylistFun && deleteMylistFun(row.productId, row.name);
          }}
        />
      ) : (
        <AssignmentOutlinedIcon
          sx={styles.icon}
          onClick={(e) => {
            e.stopPropagation();
            addMylistFun && addMylistFun(row.productId, row.name);
          }}
        />
      )}
      {displayFavoriteFlg ? (
        <FavoriteOutlinedIcon
          sx={[styles.icon, { color: pink }]}
          onClick={(e) => {
            e.stopPropagation();
            deleteFavoriteFun && deleteFavoriteFun(row.productId, row.name);
          }}
        />
      ) : (
        <FavoriteBorderOutlinedIcon
          sx={styles.icon}
          onClick={(e) => {
            e.stopPropagation();
            addFavoriteFun && addFavoriteFun(row.productId, row.name);
          }}
        />
      )}
    </>
  );
};

const MaterialGraph: React.FC<Props> = (props) => {
  const {
    list,
    addMylistFun,
    deleteMylistFun,
    addFavoriteFun,
    deleteFavoriteFun,
    offset
  } = props;

  const [payload, setPayload] = useState<ItemProps[]>();
  const [scaleY, setScaleY] = useState<number>(0);
  const [scaleX, setScaleX] = useState<number>(0);

  useEffect(() => {
    length();
  }, [list]);

  const shortenString8 = (str: string): string => {
    return str?.length > 8 ? str.slice(0, 8) + '...' : str;
  };

  const shortenString12 = (str: string): string => {
    return str?.length > 12 ? str.slice(0, 12) + '...' : str;
  };

  const shortenString = (str: string): string => {
    return str?.length > 30 ? str.slice(0, 30) + '...' : str;
  };

  const length = (): void => {
    const reductionList: number[] = [];
    const priceList: number[] = [];
    list.map((item) => {
      reductionList.push(item.reductionAmount);
      priceList.push(item.price);
    });
    const maxY = Math.max(...reductionList);
    const maxX = Math.max(...priceList);
    setScaleY(scale(maxY));
    setScaleX(scale(maxX));
  };

  function scale(maxVal: number): number {
    if (maxVal >= 1000000000) {
      return Math.ceil(maxVal / 100000000) * 100000000;
    } else if (maxVal >= 100000000) {
      return Math.ceil(maxVal / 100000000) * 100000000;
    } else if (maxVal >= 10000000) {
      return Math.ceil(maxVal / 10000000) * 10000000;
    } else if (maxVal >= 1000000) {
      return Math.ceil(maxVal / 1000000) * 1000000;
    } else if (maxVal >= 100000) {
      return Math.ceil(maxVal / 100000) * 100000;
    } else if (maxVal >= 10000) {
      return Math.ceil(maxVal / 10000) * 10000;
    } else if (maxVal >= 1000) {
      return Math.ceil(maxVal / 1000) * 1000;
    } else if (maxVal >= 100) {
      return Math.ceil(maxVal / 100) * 100;
    } else if (maxVal >= 10) {
      return Math.ceil(maxVal / 10) * 10;
    } else if (maxVal >= 1) {
      return Math.ceil(maxVal);
    } else {
      return Math.ceil(maxVal * 10) / 10;
    }
  }

  const originalList = useRef<ItemProps[]>(list);
  const [sortedList, setSortedList] = useState<ItemProps[]>(list);
  const [compareList, setCompareList] = useState<number[]>([]);
  const [compareOrder, setCompareOrder] = useState<number[]>([]);

  // 行のカラーを変更
  const rowColor = (id: number): string => {
    if (compareList.includes(id)) return green10;
    if (focusProduct === id) return gray10;
    return white;
  };

  // 比較リストに追加
  const toggleCompareCheck = (id: number): void => {
    setCompareList((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
    setCompareOrder((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // compareListに追加されたらlistの順番を変える
  const reorderItems = (): ItemProps[] => {
    const sortedByCompareList = compareOrder.map(
      (id) => originalList.current.find((item) => item.productId === id)!
    );
    const remainingItems = originalList.current.filter(
      (item) => !compareList.includes(item.productId)
    );
    return [...sortedByCompareList, ...remainingItems];
  };

  useEffect(() => {
    setSortedList(reorderItems());
  }, [compareList, compareOrder]);

  // ページング等でlistが変更された場合に、compareListをリセットする
  useEffect(() => {
    originalList.current = list;
    setSortedList(list);
    setCompareList([]);
    setCompareOrder([]);
  }, [list]);

  const [focusProduct, setFocusProduct] = useState<number | null>(null);

  const CustomTooltip = ({ payload }: TooltipProps<number, string>) => {
    if (payload && payload.length) {
      return (
        <Grid
          container
          gap={'8px'}
          sx={styles.hover}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <CloseIcon
            onClick={() => setPayload([])}
            sx={{
              color: gray90,
              cursor: 'pointer',
              height: '14px',
              right: '8px',
              position: 'absolute',
              top: '8px',
              width: '14px',
              zIndex: 9999
            }}
          />

          <Grid container flexDirection={'row'} flexWrap={'nowrap'} gap={'8px'}>
            <CardMedia
              component="img"
              image={payload[0]?.payload?.images[0]?.filepath ?? noImage}
              alt={`${payload[0].payload.name}`}
              sx={styles.hoverDataImage}
            />
            <Grid container flexDirection={'column'}>
              <Link
                sx={styles.hoverDataLink}
                href={`/material/${payload[0].payload.productId}`}
                target={'_blank'}
              >
                <Typography sx={styles.hoverDataTitle}>
                  {shortenString(`${payload[0].payload.alias}`)}
                </Typography>

                <Typography sx={[styles.hoverDataTitle, { fontWeight: 700 }]}>
                  {shortenString(`${payload[0].payload.name}`)}
                </Typography>
              </Link>
            </Grid>
          </Grid>
          <Grid
            container
            flexDirection="row"
            justifyContent="space-between"
            alignItems="flex-end"
          >
            <Grid
              container
              flexDirection={'column'}
              flexWrap={'nowrap'}
              gap={'4px'}
              mt={'8px'}
              width={'unset'}
            >
              <Typography sx={styles.hoverDataText}>
                価格(円)：{' '}
                {payload[0].value === 0 ? 'ー' : `${payload[0].value}`}
              </Typography>
              <Typography sx={styles.hoverDataText}>
                CO2原単位： {`${payload[1].value}`}
              </Typography>{' '}
            </Grid>
            <Grid flexDirection={'row'}>
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{
                      '&.Mui-checked': {
                        color: green50
                      },
                      color: gray90,
                      p: '0 4px'
                    }}
                    checked={compareList.includes(payload[0].payload.productId)}
                    onChange={() =>
                      toggleCompareCheck(payload[0].payload.productId)
                    }
                  />
                }
                label={'比較する'}
                sx={{ span: { fontSize: '14px' }, color: gray100, m: 0 }}
              />
            </Grid>
          </Grid>
        </Grid>
      );
    }
    return null;
  };

  const getStickyCellStyle = (width: number, zIndex: number, left: number) => {
    return {
      position: 'sticky',
      left: left,
      background: 'white',
      minWidth: width,
      zIndex: zIndex
    };
  };

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [subtractionWidth, setSubtractionWidth] = useState(613);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    if (window.innerWidth >= 1360) {
      setSubtractionWidth(613);
    } else if (window.innerWidth < 1360 && window.innerWidth > 960) {
      setSubtractionWidth(300);
    } else if (window.innerWidth <= 960) {
      setSubtractionWidth(70);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [windowWidth]);

  useEffect(() => {
    setPayload([]);
  }, [list]);

  useEffect(() => {
    setPayload([]);
  }, [compareList]);

  useEffect(() => {
    if (!payload || payload.length === 0) {
      setFocusProduct(null);
    }
  }, [payload]);

  return (
    <>
      <Box sx={{ width: '100%' }} onClick={() => setPayload([])}>
        <ResponsiveContainer width={'100%'} height={600}>
          <ScatterChart
            margin={{
              top: 40,
              right: 85,
              bottom: 20,
              left: 20
            }}
          >
            <CartesianGrid stroke={gray70} />
            <XAxis
              type="number"
              dataKey="price"
              name="価格(円)"
              unit=""
              stroke={gray70}
              tick={{ fill: gray100, fontSize: 14 }}
              domain={[0, scaleX]}
              ticks={Array.from({ length: 11 }, (_, i) => i * (scaleX / 10))}
              tickFormatter={(value) => {
                if (scaleX < 10) {
                  if (value === 0) {
                    return value;
                  }
                  return value.toFixed(1);
                }
                return value;
              }}
            >
              <Label
                value="価格(円)"
                offset={30}
                position="right"
                style={{ fontSize: 14, fontWeight: 500, fill: gray100 }}
              />
            </XAxis>
            <YAxis
              type="number"
              dataKey="reductionAmount"
              name="CO2原単位"
              unit=""
              stroke={gray70}
              tick={{ fill: gray100, fontSize: 14 }}
              domain={[0, scaleY]}
              ticks={Array.from({ length: 11 }, (_, i) => i * (scaleY / 10))}
              tickFormatter={(value) => {
                if (scaleY < 1) {
                  if (value === 0) {
                    return value;
                  }
                  return value.toFixed(2);
                } else if (scaleY < 10) {
                  if (value === 0) {
                    return value;
                  }
                  return value.toFixed(1);
                }
                return value;
              }}
            >
              <Label
                value="CO2原単位"
                offset={20}
                position="top"
                style={{ fontSize: 14, fontWeight: 500, fill: gray100 }}
              />
            </YAxis>
            <Tooltip
              wrapperStyle={{
                visibility: payload?.length !== 0 ? 'visible' : 'hidden'
              }}
              content={<CustomTooltip payload={payload} />}
              cursor={{ strokeDasharray: '3 3' }}
            />
            <Scatter
              name="graph"
              data={list}
              onMouseEnter={(data) => {
                setPayload(data);
                setFocusProduct(data.payload.productId);
              }}
              onMouseOver={(data) => {
                setPayload(data);
                setFocusProduct(data.payload.productId);
              }}
            >
              {list.map((entry: ItemProps, index: number) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    compareList.includes(entry.productId)
                      ? green50
                      : entry.price === 0
                      ? 'grey'
                      : primaryColor
                  }
                  width={'7px'}
                  height={'7px'}
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </Box>
      {compareList.length > 0 ? (
        <Grid
          container
          flexDirection={'row'}
          gap={'14px'}
          alignItems={'center'}
          mb={'12px'}
        >
          <Typography sx={{ fontSize: '14px' }}>
            選択中：{compareList.length}件
          </Typography>
          <Button
            variant="outlined"
            sx={{
              color: green50,
              border: `1px solid ${green50}`,
              ':hover': {
                bgcolor: green10,
                border: `1px solid ${green50}`
              }
            }}
            onClick={() => {
              setCompareList([]);
              setSortedList(list);
              setCompareOrder([]);
            }}
          >
            <Typography sx={{ color: green50 }}>選択クリア</Typography>
          </Button>
        </Grid>
      ) : (
        <></>
      )}
      <Box
        sx={{
          width: '100%'
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 0,
            width: windowWidth - subtractionWidth,
            overflowX: 'scroll'
          }}
        >
          <Table
            stickyHeader
            sx={[styles.table, { minWidth: windowWidth - subtractionWidth }]}
            aria-label="simple table"
          >
            <TableHead sx={{ bgcolor: primary10 }}>
              <TableRow>
                <TableCell sx={getStickyCellStyle(20, 90, 0)}>No</TableCell>
                <TableCell sx={getStickyCellStyle(172, 60, 49)}>
                  一般名称
                </TableCell>
                <TableCell sx={getStickyCellStyle(179, 180, 244)}>
                  商品名
                </TableCell>
                <TableCell sx={styles.tableCellItemSpecification}>
                  仕様
                </TableCell>
                <TableCell sx={styles.tableCellItemEmissionIntensity}>
                  CO2原単位
                </TableCell>
                <TableCell sx={styles.tableCellItemReduceRate}>
                  CO2削減率
                </TableCell>
                <TableCell sx={styles.tableCellItemPrice}>
                  参考設計単価
                </TableCell>
                <TableCell sx={styles.tableCellItemCompany}>会社名</TableCell>
                <TableCell sx={styles.tableCellItemButton}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedList.map((row, index: number) => (
                <TableRow
                  key={index}
                  onClick={() =>
                    window.open(`/material/${row.productId}`, '_blank')
                  }
                  sx={{ bgcolor: rowColor(row.productId) }}
                >
                  <TableCell
                    component="td"
                    scope="row"
                    sx={[
                      getStickyCellStyle(20, 90, 0),
                      { bgcolor: rowColor(row.productId) }
                    ]}
                  >
                    {index + 1 + offset}
                  </TableCell>
                  <TableCell
                    sx={[
                      getStickyCellStyle(172, 60, 49),
                      { bgcolor: rowColor(row.productId) }
                    ]}
                  >
                    {shortenString12(row.alias)}
                  </TableCell>
                  <TableCell
                    sx={[
                      styles.tableOverflow,
                      getStickyCellStyle(179, 180, 244),
                      { bgcolor: rowColor(row.productId) }
                    ]}
                  >
                    {shortenString12(row.name)}
                  </TableCell>
                  <TableCell sx={styles.tableOverflow}>
                    {shortenString8(row.specification)}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Typography component="span" sx={styles.tableCellTextBlue}>
                      {row.calculatedEmissionIntensity
                        ? row.calculatedEmissionIntensity
                        : 'ー'}
                    </Typography>
                    <br />
                    <Typography component="span" sx={styles.tableCellText}>
                      kg-CO2/{row.co2Unit}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    {row.calculatedReduceRate !== null &&
                    row.calculatedReduceRate !== undefined ? (
                      <>
                        <ChangeHistoryOutlinedIcon sx={styles.co2ValueIcon} />
                        <Typography
                          component="span"
                          sx={styles.tableCellTextBlue}
                        >
                          {row.calculatedReduceRate}
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        component="span"
                        sx={styles.tableCellTextBlue}
                      >
                        ー
                      </Typography>
                    )}
                    <Typography component="span" sx={styles.tableCellText}>
                      %
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>
                    {row.referenceUnitPrice?.toLocaleString()}
                  </TableCell>
                  <TableCell sx={styles.tableOverflow}>
                    {row.companyShort}
                  </TableCell>
                  <TableCell>
                    <Grid
                      container
                      flexDirection={'row'}
                      gap={'12px'}
                      flexWrap={'nowrap'}
                    >
                      <MyListAndFavorite
                        row={row}
                        deleteMylistFun={deleteMylistFun}
                        addMylistFun={addMylistFun}
                        deleteFavoriteFun={deleteFavoriteFun}
                        addFavoriteFun={addFavoriteFun}
                      />
                    </Grid>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};
export default MaterialGraph;
